/**
 * Types and functions for building and working with 2d rectangles
 * @module
 */
import type { AABB2 } from "./aabb2Types";
import { DIST_EPSILON } from "./core";
import { line2Normal, line2Len } from "./line2";
import { line2VecPerp } from "./line2Polyline2";
import {
  polyLine2Copy,
  polyLine2EdgeLength,
  polyLine2EdgeNormal,
  polyLine2Move,
  polyLine2RepairEdgeIndex,
  polyLine2Reverse,
} from "./polyLine2";
import type { PolyLine2 } from "./polyLine2Types";
import { polyLine2New } from "./polyLine2Types";
import { ray2Project } from "./ray2";
import type { Rect2 } from "./rect2Types";
import { sumArray } from "./utils";
import { vec2Dist, vec2Lerp } from "./vec2";

export function rect2ByCenterWidthHeight(centerX: number, centerY: number, width: number, height: number): Rect2 {
  return rect2ByTwoPoints(centerX - width / 2, centerY - height / 2, centerX + width / 2, centerY + height / 2);
}

export function rect2ByBottomCenterWidthHeightNormal(
  bottomCenterX: number,
  bottomCenterY: number,
  width: number,
  height: number,
  normalX: number,
  normalY: number,
) {
  const halfwidthX = (width / 2) * normalX;
  const halfwidthY = (width / 2) * normalY;
  const heightX = height * normalY;
  const heightY = height * -normalX;

  const botlx = bottomCenterX - halfwidthX;
  const botly = bottomCenterY - halfwidthY;
  const botrx = bottomCenterX + halfwidthX;
  const botry = bottomCenterY + halfwidthY;
  const toprx = bottomCenterX + halfwidthX + heightX;
  const topry = bottomCenterY + halfwidthY + heightY;
  const toplx = bottomCenterX - halfwidthX + heightX;
  const toply = bottomCenterY - halfwidthY + heightY;

  return polyLine2New([botlx, botly, botrx, botry, toprx, topry, toplx, toply, botlx, botly]);
}

export function rect2ByBottomLeftWidthHeight(
  bottomLeftX: number,
  bottomLeftY: number,
  width: number,
  height: number,
): Rect2 {
  return rect2ByTwoPoints(bottomLeftX, bottomLeftY, bottomLeftX + width, bottomLeftY + height);
}

export function rect2ByBottomLeftWidthHeightNormal(
  bottomLeftX: number,
  bottomLeftY: number,
  width: number,
  height: number,
  normalX: number,
  normalY: number,
): Rect2 {
  const widthX = -width * normalX;
  const widthY = -width * normalY;
  const heightX = height * normalY;
  const heightY = height * -normalX;

  const botlx = bottomLeftX;
  const botly = bottomLeftY;
  const botrx = bottomLeftX + widthX;
  const botry = bottomLeftY + widthY;
  const toprx = bottomLeftX + widthX + heightX;
  const topry = bottomLeftY + widthY + heightY;
  const toplx = bottomLeftX + heightX;
  const toply = bottomLeftY + heightY;

  return polyLine2New([botlx, botly, botrx, botry, toprx, topry, toplx, toply, botlx, botly]);
}

export function rect2ByTwoPoints(ax: number, ay: number, bx: number, by: number): Rect2 {
  if (ax > bx) {
    const t = ax;
    ax = bx;
    bx = t;
  }

  if (ay > by) {
    const t = ay;
    ay = by;
    by = t;
  }

  return polyLine2New([ax, ay, bx, ay, bx, by, ax, by, ax, ay]);
}

export function rect2ByThreePoints(
  bottomStartX: number,
  bottomStartY: number,
  bottomEndX: number,
  bottomEndY: number,
  topX: number,
  topY: number,
): Rect2 {
  const [nx, ny] = line2Normal(bottomStartX, bottomStartY, bottomEndX, bottomEndY, true);

  const [toplx, toply] = ray2Project(bottomStartX, bottomStartY, nx, ny, topX, topY);
  const [toprx, topry] = ray2Project(bottomEndX, bottomEndY, nx, ny, topX, topY);

  return polyLine2New([
    bottomStartX,
    bottomStartY,
    bottomEndX,
    bottomEndY,
    toprx,
    topry,
    toplx,
    toply,
    bottomStartX,
    bottomStartY,
  ]);
}

export function rect2ByAABB2(bb: AABB2): Rect2 {
  return rect2ByTwoPoints(bb.minX, bb.minY, bb.maxX, bb.maxY);
}

/** rect2Split splits a rectangle along a vertical or horizontal axis. Returns two new Rect2s.
for a vertical split, [left, right]
for a horizontal split, [bottom, top]
 */

export function rect2Split(rect: PolyLine2, vertical: boolean, t: number, normalized: boolean): Rect2[] {
  if (vertical) {
    const l = vec2Dist(rect[0], rect[1], rect[2], rect[3]);

    if (!normalized) {
      t = t / l;
    }

    if (t < 0) {
      t = 1.0 + t;
    }

    const [startX, startY] = vec2Lerp(rect[0], rect[1], rect[2], rect[3], t);
    const [endX, endY] = vec2Lerp(rect[6], rect[7], rect[4], rect[5], t);

    const left = polyLine2New([rect[0], rect[1], startX, startY, endX, endY, rect[6], rect[7], rect[0], rect[1]]);
    const right = polyLine2New([startX, startY, rect[2], rect[3], rect[4], rect[5], endX, endY, startX, startY]);

    return [left, right];
  } else {
    const l = vec2Dist(rect[0], rect[1], rect[6], rect[7]);

    if (!normalized) {
      t = t / l;
    }

    if (t < 0) {
      t = 1.0 + t;
    }

    const [startX, startY] = vec2Lerp(rect[0], rect[1], rect[6], rect[7], t);
    const [endX, endY] = vec2Lerp(rect[2], rect[3], rect[4], rect[5], t);

    const bottom = polyLine2New([rect[0], rect[1], rect[2], rect[3], endX, endY, startX, startY, rect[0], rect[1]]);
    const top = polyLine2New([startX, startY, endX, endY, rect[4], rect[5], rect[6], rect[7], startX, startY]);

    return [bottom, top];
  }
}

/** rect2SplitFromEdge splits the rectangle parallel to the edge parameter and t away from that edge */
export function rect2SplitWithBaseEdge(rect: PolyLine2, edge: number, t: number, normalized: boolean): Rect2[] | null {
  edge = edge + (4 % 4);

  switch (edge) {
    case 0: {
      return rect2Split(rect, false, t, normalized);
    }
    case 1: {
      return rect2Split(rect, true, -t, normalized).reverse();
    }
    case 2: {
      return rect2Split(rect, false, -t, normalized).reverse();
    }
    case 3: {
      return rect2Split(rect, true, t, normalized);
    }
  }
  return null;
}

/** rect2MultiSplit splits a rectangle along an axis multiple times. The parameters are sorted split points. */
export function rect2MultiSplit(rect: PolyLine2, vertical: boolean, params: number[], normalized: boolean): Rect2[] {
  const rects: Rect2[] = [];

  let lrect: Rect2;
  let cur = rect;

  if (normalized) {
    const l = vertical ? vec2Dist(rect[0], rect[1], rect[2], rect[3]) : vec2Dist(rect[0], rect[1], rect[6], rect[7]);

    normalized = false;
    params = params.map((x, i) => (i === 0 ? l * x : l * (x - params[i - 1])));
  } else {
    params = params.map((x, i) => (i === 0 ? x : x - params[i - 1]));
  }

  for (const t of params) {
    [lrect, cur] = rect2Split(cur, vertical, t, normalized);
    rects.push(lrect);
  }

  rects.push(cur);

  return rects;
}

/** rect2MultiSplitByWidths splits a rectangle along an axis multiple times with the given widths */

export function rect2MultiSplitByWidths(rect: PolyLine2, vertical: boolean, widths: number[]): Rect2[] {
  const rects: Rect2[] = [];

  let lrect: Rect2;
  let cur = rect;

  for (const t of widths) {
    [lrect, cur] = rect2Split(cur, vertical, t, false);
    rects.push(lrect);
  }

  rects.push(cur);

  let len;

  if (vertical) {
    len = line2Len(rect[0], rect[1], rect[2], rect[3]);
  } else {
    len = line2Len(rect[2], rect[3], rect[4], rect[5]);
  }

  // remove the erronous last rectangle if the width list is the same length as the length to divide.
  const totalWidth = sumArray(widths);
  if (Math.abs(totalWidth - len) < DIST_EPSILON) {
    rects.pop();
  }

  return rects;
}

/** helper to create equal divisions in spaces when 0 is passed to widths */

export function rect2MultiSplitFlex(rect: Rect2, values: number[], vertical: boolean) {
  // zero values are equal divisions
  const widths: number[] = [];

  let divs = 0;
  let len = 0;

  if (!vertical) {
    len = polyLine2EdgeLength(rect, 1);
  } else {
    len = polyLine2EdgeLength(rect, 2);
  }

  for (const value of values) {
    if (value === 0) {
      divs += 1;
    } else {
      len -= value;
    }
  }

  for (const value of values) {
    if (value === 0) {
      widths.push(len / divs);
    } else {
      widths.push(value);
    }
  }

  const rects = rect2MultiSplitByWidths(rect, vertical, widths);
  return rects;
}

// rectMultiSplitUniform splits a rectangle along multiple times with regular spacing

export function rect2UniformMultiSplit(rect: PolyLine2, vertical: boolean, count: number): Rect2[] {
  if (count <= 1) {
    return [rect];
  }

  const ts: number[] = [];

  const step = 1.0 / count;
  let cur = step;

  for (let i = 0; i < count - 1; i++) {
    ts.push(cur);
    cur += step;
  }

  return rect2MultiSplit(rect, vertical, ts, true);
}

// rect2Edge extracts a given edge from a rect. it supports negative indexing and overindexing

export function rect2Extend(rect: Rect2, edge: number, dist: number): Rect2 {
  const index = polyLine2RepairEdgeIndex(rect, edge);
  const [normX, normY] = polyLine2EdgeNormal(rect, index);

  const i2 = index * 2;

  rect[i2] = rect[i2] + normX * dist;
  rect[i2 + 1] = rect[i2 + 1] + normY * dist;

  rect[i2 + 2] = rect[i2 + 2] + normX * dist;
  rect[i2 + 3] = rect[i2 + 3] + normY * dist;

  if (index === 0) {
    rect[8] = rect[0];
    rect[9] = rect[1];
  }

  if (index === 3) {
    rect[0] = rect[8];
    rect[1] = rect[9];
  }

  return rect;
}

export function rect2ByLine2AndThickness(
  x0: number,
  y0: number,
  x1: number,
  y1: number,
  rightOffset: number,
  leftOffset: number,
  flip?: boolean,
  rotate?: boolean,
  shift?: number,
): PolyLine2 {
  const line = flip ? [x1, y1, x0, y0] : [x0, y0, x1, y1];
  const [pVecX, pVecY] = line2Normal(line[0], line[1], line[2], line[3]);

  const rightLine = [
    line[0] + pVecX * rightOffset,
    line[1] + pVecY * rightOffset,
    line[2] + pVecX * rightOffset,
    line[3] + pVecY * rightOffset,
  ];

  const leftLine = [
    line[0] - pVecX * leftOffset,
    line[1] - pVecY * leftOffset,
    line[2] - pVecX * leftOffset,
    line[3] - pVecY * leftOffset,
  ];

  let rect = polyLine2New(10);

  if (!rotate) {
    rect[0] = rightLine[0];
    rect[1] = rightLine[1];

    rect[2] = leftLine[0];
    rect[3] = leftLine[1];

    rect[4] = leftLine[2];
    rect[5] = leftLine[3];

    rect[6] = rightLine[2];
    rect[7] = rightLine[3];

    rect[8] = rect[0];
    rect[9] = rect[1];
  } else {
    rect[0] = rightLine[0];
    rect[1] = rightLine[1];

    rect[2] = rightLine[2];
    rect[3] = rightLine[3];

    rect[4] = leftLine[2];
    rect[5] = leftLine[3];

    rect[6] = leftLine[0];
    rect[7] = leftLine[1];

    rect[8] = rect[0];
    rect[9] = rect[1];
  }

  rect = shiftPolyLine2(rect, shift);
  return rect;
}

function shiftPolyLine2(list: PolyLine2, index: number = 0): PolyLine2 {
  const array = Array.from(list);
  const splitList = array.slice(0, 8);

  const firstPart = splitList.slice(0, index);
  const secondPart = splitList.slice(index, splitList.length);

  const rawList = secondPart.concat(firstPart);

  const newList = polyLine2New(list.length);

  const listLength = list.length;

  for (let i = 0; i < rawList.length; i++) {
    newList[i] = rawList[i];
  }

  newList[listLength - 2] = newList[0];
  newList[listLength - 1] = newList[1];

  return newList;
}

///////////////////////////// BELOW THIS LINE - MERGED FROM HOUSE/RECT (QC REQUIRED)

export function getLine(pl: PolyLine2, index: number): PolyLine2 {
  const theLine = polyLine2New(4);

  index *= 2;
  theLine[0] = pl[index];
  theLine[1] = pl[index + 1];
  theLine[2] = pl[index + 2];
  theLine[3] = pl[index + 3];

  return theLine;
}

export function makeRectFromLine(
  polyLine: PolyLine2,
  rightOffset: number,
  leftOffset: number,
  flip?: boolean,
  rotate?: boolean,
  shift?: number,
): PolyLine2 {
  let pl = polyLine2Copy(polyLine);
  if (flip) {
    pl = polyLine2Reverse(pl);
  }

  const pl1 = polyLine2Copy(pl);
  const pl2 = polyLine2Copy(pl);

  const pVec = line2VecPerp(pl);

  polyLine2Move(pl1, -pVec[0] * rightOffset, -pVec[1] * rightOffset);
  polyLine2Move(pl2, pVec[0] * leftOffset, pVec[1] * leftOffset);

  let rect = polyLine2New(10);

  if (!rotate) {
    rect[0] = pl1[0];
    rect[1] = pl1[1];

    rect[2] = pl2[0];
    rect[3] = pl2[1];

    rect[4] = pl2[2];
    rect[5] = pl2[3];

    rect[6] = pl1[2];
    rect[7] = pl1[3];

    rect[8] = rect[0];
    rect[9] = rect[1];
  } else {
    rect[0] = pl1[0];
    rect[1] = pl1[1];

    rect[2] = pl1[2];
    rect[3] = pl1[3];

    rect[4] = pl2[2];
    rect[5] = pl2[3];

    rect[6] = pl2[0];
    rect[7] = pl2[1];

    rect[8] = rect[0];
    rect[9] = rect[1];
  }

  rect = shiftPolyLine2(rect, shift);
  return rect;
}
